/*
  Bellamy & Laurent cart drawer extras: reservation timer, product upsell and the
  discount-code field.

  Dawn re-renders the whole .drawer__inner from the cart-drawer section after every
  cart change, so each piece is a custom element that wires itself up again whenever
  fresh markup lands. Nothing here holds state across renders except the timer's end
  time, which lives in sessionStorage.
*/
(function () {
  var TIMER_KEY = 'hlCartTimerEnd';
  var INVALID_CODE = 'The discount code you entered is invalid or cannot be applied to this cart';
  var memoryEnd = 0; // fallback when sessionStorage is unavailable

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function drawer() {
    return document.querySelector('cart-drawer');
  }

  function sectionIds(el) {
    return el && typeof el.getSectionsToRender === 'function'
      ? el.getSectionsToRender().map(function (s) { return s.id; })
      : ['cart-drawer', 'cart-icon-bubble'];
  }

  function postCart(endpoint, payload) {
    var el = drawer();
    payload.sections = sectionIds(el);
    payload.sections_url = window.location.pathname;
    return fetch(root() + endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(function (res) {
        return res.json().then(function (state) {
          if (!res.ok || state.status) {
            throw new Error(state.description || state.message || 'Something went wrong. Please try again.');
          }
          return state;
        });
      });
  }

  // The drawer is fully re-rendered from the response here, so the event goes out
  // as 'cart-items': CartItems skips its own re-fetch for that source, everything
  // else (the timer below, the product form's "in cart" count) still hears it.
  function renderAndAnnounce(state) {
    var el = drawer();
    if (el && typeof el.renderContents === 'function') el.renderContents(state);
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: state });
    }
  }

  /* ---------------- Reservation timer ---------------- */

  function readEnd() {
    try {
      return parseInt(sessionStorage.getItem(TIMER_KEY) || '0', 10) || memoryEnd;
    } catch (e) {
      return memoryEnd;
    }
  }

  function restartTimer(ms) {
    memoryEnd = Date.now() + ms;
    try { sessionStorage.setItem(TIMER_KEY, String(memoryEnd)); } catch (e) {}
  }

  if (!customElements.get('hl-countdown')) {
    customElements.define('hl-countdown', class extends HTMLElement {
      connectedCallback() {
        this.duration = (parseInt(this.dataset.duration || '600', 10) || 600) * 1000;
        if (readEnd() <= Date.now()) restartTimer(this.duration);
        this.tick = this.tick.bind(this);
        this.tick();
        this.interval = setInterval(this.tick, 1000);
      }

      disconnectedCallback() {
        clearInterval(this.interval);
      }

      tick() {
        var left = readEnd() - Date.now();
        if (left <= 0) {
          restartTimer(this.duration);
          left = this.duration;
        }
        var sec = Math.ceil(left / 1000);
        this.textContent = String(Math.floor(sec / 60)).padStart(2, '0') + ':' + String(sec % 60).padStart(2, '0');
      }
    });
  }

  // Like the reference cart, every change to the cart starts the 10 minutes over.
  function subscribeTimerReset() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
      var el = document.querySelector('hl-countdown');
      restartTimer(((el && parseInt(el.dataset.duration, 10)) || 600) * 1000);
      document.querySelectorAll('hl-countdown').forEach(function (c) {
        if (typeof c.tick === 'function') c.tick();
      });
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', subscribeTimerReset);
  } else {
    subscribeTimerReset();
  }

  /* ---------------- Product upsell ---------------- */

  if (!customElements.get('hl-cart-upsell')) {
    customElements.define('hl-cart-upsell', class extends HTMLElement {
      connectedCallback() {
        this.cards = Array.prototype.slice.call(this.querySelectorAll('[data-slide]'));
        this.prev = this.querySelector('[data-dir="-1"]');
        this.next = this.querySelector('[data-dir="1"]');
        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('change', this.onChange.bind(this));
        this.show(0);
      }

      show(index) {
        var last = this.cards.length - 1;
        this.index = Math.max(0, Math.min(index, last));
        this.cards.forEach(function (card, i) { card.hidden = i !== this.index; }, this);
        if (this.prev) this.prev.disabled = this.index === 0;
        if (this.next) this.next.disabled = this.index === last;
      }

      onClick(event) {
        var arrow = event.target.closest('[data-dir]');
        if (arrow) {
          this.show(this.index + parseInt(arrow.dataset.dir, 10));
          return;
        }
        var add = event.target.closest('[data-hl-add]');
        if (add) this.add(add);
      }

      // A variant change only moves the price, saving badge and the id the button adds.
      onChange(event) {
        var select = event.target.closest('[data-hl-variant]');
        if (!select) return;
        var option = select.options[select.selectedIndex];
        var card = select.closest('[data-slide]');
        var compare = card.querySelector('[data-hl-compare]');
        var save = card.querySelector('[data-hl-save]');
        card.querySelector('[data-hl-price]').textContent = option.dataset.price;
        compare.textContent = option.dataset.compare || '';
        compare.hidden = !option.dataset.compare;
        save.textContent = option.dataset.save ? 'You save ' + option.dataset.save + '%' : '';
        save.hidden = !option.dataset.save;
        card.querySelector('[data-hl-add]').dataset.variantId = option.value;
      }

      add(button) {
        var card = button.closest('[data-slide]');
        var error = card.querySelector('[data-hl-error]');
        error.hidden = true;
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        postCart('cart/add.js', { items: [{ id: Number(button.dataset.variantId), quantity: 1 }] })
          .then(renderAndAnnounce)
          .catch(function (e) {
            error.textContent = e.message;
            error.hidden = false;
            button.disabled = false;
            button.removeAttribute('aria-busy');
          });
      }
    });
  }

  /* ---------------- Discount code ---------------- */

  // cart/update.js reports codes in discount_codes[] with an applicable flag.
  function codeApplies(state, code) {
    var wanted = code.toLowerCase();
    var list = state.discount_codes || [];
    for (var i = 0; i < list.length; i++) {
      if (String(list[i].code).toLowerCase() === wanted) return !!list[i].applicable;
    }
    return false;
  }

  function showDiscountError(message) {
    var box = document.querySelector('hl-cart-discount [data-hl-discount-error]');
    if (!box) return;
    box.textContent = message;
    box.hidden = false;
  }

  if (!customElements.get('hl-cart-discount')) {
    customElements.define('hl-cart-discount', class extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.input = this.querySelector('input');
        this.button = this.querySelector('[data-hl-apply]');
        this.error = this.querySelector('[data-hl-discount-error]');
        if (!this.form) return;
        this.input.addEventListener('input', this.onInput.bind(this));
        this.form.addEventListener('submit', this.onSubmit.bind(this));
        this.addEventListener('click', this.onClick.bind(this));
      }

      codes() {
        return (this.dataset.codes || '').split(',').map(function (c) { return c.trim(); }).filter(Boolean);
      }

      onInput() {
        this.button.disabled = !this.input.value.trim();
        this.error.hidden = true;
      }

      onSubmit(event) {
        event.preventDefault();
        var code = this.input.value.trim();
        if (!code) return;
        var current = this.codes();
        var already = current.some(function (c) { return c.toLowerCase() === code.toLowerCase(); });
        if (already) {
          showDiscountError('This code is already applied.');
          return;
        }
        this.busy(true);
        var self = this;
        postCart('cart/update.js', { discount: current.concat(code).join(',') })
          .then(function (state) {
            if (codeApplies(state, code)) return renderAndAnnounce(state);
            // Take the rejected code off again so it does not linger on the cart.
            return postCart('cart/update.js', { discount: current.join(',') }).then(function (clean) {
              renderAndAnnounce(clean);
              showDiscountError(INVALID_CODE);
            });
          })
          .catch(function (e) {
            self.busy(false);
            showDiscountError(e.message);
          });
      }

      onClick(event) {
        var remove = event.target.closest('[data-hl-remove-code]');
        if (!remove) return;
        var code = remove.dataset.hlRemoveCode.toLowerCase();
        var rest = this.codes().filter(function (c) { return c.toLowerCase() !== code; });
        remove.disabled = true;
        postCart('cart/update.js', { discount: rest.join(',') })
          .then(renderAndAnnounce)
          .catch(function (e) {
            remove.disabled = false;
            showDiscountError(e.message);
          });
      }

      busy(on) {
        this.input.disabled = on;
        this.button.disabled = on || !this.input.value.trim();
        this.toggleAttribute('aria-busy', on);
      }
    });
  }
})();
